import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import ProgressWizard from 'components/field/ProgressWizard';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { AUTOCOMPLETE, SHIPPING_PRICES } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faAngleLeft, faAngleRight, faBookOpen, faKey } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author @muhammedburakak
 */

const ShipmentDetails = ({ isLoading, setIsLoading }) => {
	const axios = useAxiosPrivate();
	const { showError } = useAlert();
	const defaultColSpan = {
		md: 6
	};

	const formMethods = useFormContext();
	const {
		watch,
		reset,
		setValue,
		getValues,
		formState: { errors }
	} = formMethods;

	const getRecordData = useCallback(() => {
		axios
			.post(SHIPPING_PRICES.GET_INVOICE_TRANSACTION_RECORD, {
				shipmentID: getValues('shipmentNumber')
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					if (!data.shipmentID) {
						showError({
							message: 'errorMsg.noRecordFound'
						});
					}

					reset({
						...data,
						shipmentNumber: data.shipmentID
					});
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [watch('shipmentNumber')]);

	useEffect(() => {
		if (watch('shipmentNumber')) {
			getRecordData();
		}
	}, [watch('shipmentNumber')]);

	const invoiceShimentIDAutocompleteColumns = useMemo(() => [{ accessor: 'ID', width: 200 }], []);

	return (
		<>
			<FormItem colSpan={defaultColSpan} label="shipmentNumber" isLoading={isLoading} required>
				<AutoComplete
					name="shipmentNumber"
					autoCompleteName={AUTOCOMPLETE.INVOICE_TRANSACTIONS_SHIPMENTID}
					columns={invoiceShimentIDAutocompleteColumns}
					selectionLabelAccessor={['ID']}
					value={watch(`shipmentNumber`)}
					setValue={(value) => {
						setValue('shipmentNumber', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="orderNumber" isLoading={isLoading}>
				<StringInput disabled={true} name="orderID" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="customerName" isLoading={isLoading}>
				<StringInput disabled={true} name="customerName" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="orderCreateDate" isLoading={isLoading}>
				<DateInput
					name="orderCreateDate"
					value={watch('orderCreateDate')}
					disabled={true}
					setValue={(value) => {
						setValue('orderCreateDate', value, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="serviceType" isLoading={isLoading}>
				<EnumSelect
					searchable={false}
					name="serviceType"
					disabled={true}
					enumName="PriceConfigurationServiceType"
					setValue={(value) => {
						setValue(`serviceType`, value, { shouldValidate: true, shouldDirty: true });
					}}
					value={watch(`serviceType`)}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="deliveryCompanyName" isLoading={isLoading}>
				<StringInput disabled={true} name="deliveryCompanyName" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="shipmentCreationDate" isLoading={isLoading}>
				<DateInput
					name="shipmentCreationDate"
					value={watch('shipmentCreationDate')}
					disabled={true}
					setValue={(value) => {
						setValue('shipmentCreationDate', value, { shouldValidate: true, shouldDirty: true });
					}}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="deliveryPriceID" isLoading={isLoading}>
				<StringInput disabled={true} name="deliveryPriceID" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="chargingStatus" isLoading={isLoading}>
				<EnumSelect
					searchable={false}
					name="chargingStatus"
					disabled={true}
					enumName="PriceConfigurationChargingStatus"
					setValue={(value) => {
						setValue(`chargingStatus`, value, { shouldValidate: true, shouldDirty: true });
					}}
					value={watch(`chargingStatus`)}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="basePrice" isLoading={isLoading}>
				<CurrencyInput
					name="basePrice"
					disabled={true}
					value={watch('currency')}
					setValue={(value) => {
						setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
					}}
					currencyErrorMessage={errors?.currency?.message}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="returnFee" isLoading={isLoading}>
				<CurrencyInput
					name="returnFee"
					disabled={true}
					value={watch('currency')}
					setValue={(value) => {
						setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
					}}
					currencyErrorMessage={errors?.currency?.message}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="codFee" isLoading={isLoading}>
				<CurrencyInput
					name="codFee"
					disabled={true}
					value={watch('currency')}
					setValue={(value) => {
						setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
					}}
					currencyErrorMessage={errors?.currency?.message}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="extraKMFee" isLoading={isLoading}>
				<CurrencyInput
					name="extraKMFee"
					disabled={true}
					value={watch('currency')}
					setValue={(value) => {
						setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
					}}
					currencyErrorMessage={errors?.currency?.message}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="extraKGFee" isLoading={isLoading}>
				<CurrencyInput
					name="extraKGFee"
					disabled={true}
					value={watch('currency')}
					setValue={(value) => {
						setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
					}}
					currencyErrorMessage={errors?.currency?.message}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="insuranceFee" isLoading={isLoading}>
				<CurrencyInput
					name="insuranceFee"
					disabled={true}
					value={watch('currency')}
					setValue={(value) => {
						setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
					}}
					currencyErrorMessage={errors?.currency?.message}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="discountAmount" isLoading={isLoading}>
				<CurrencyInput
					name="discountAmount"
					disabled={true}
					value={watch('currency')}
					setValue={(value) => {
						setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
					}}
					currencyErrorMessage={errors?.currency?.message}
				/>
			</FormItem>
		</>
	);
};

const RecordCalculation = ({ isLoading }) => {
	const formMethods = useFormContext();
	const [isExtraCharge, setIsExtraCharge] = useState(null);

	const {
		watch,
		getValues,
		setValue,

		formState: { errors }
	} = formMethods;

	useEffect(() => {
		if (watch('recordType') === 'posChargeCorrection' || watch('recordType') === 'underChargeCorrection') {
			setIsExtraCharge(false);
		} else if (
			watch('recordType') === 'insuranceChargeBack' ||
			watch('recordType') === 'slaCorrection' ||
			watch('recordType') === 'overChargeCorrection'
		) {
			setIsExtraCharge(true);
		} else {
			setIsExtraCharge(null);
		}
	}, [watch('recordType')]);

	const defaultColSpan = {
		md: 6
	};
	return (
		<>
			<FormItem colSpan={defaultColSpan} label="recordType" isLoading={isLoading} required>
				<EnumSelect
					searchable={false}
					name="recordType"
					enumName="recordType"
					setValue={(value) => {
						setValue(`recordType`, value, { shouldValidate: true, shouldDirty: true });
					}}
					value={watch(`recordType`)}
				/>
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="status">
				<Status value={getValues('invoiceStatus')} />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="recordDescription" isLoading={isLoading} required>
				<TextArea name="recordDescription" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="shipmentID" isLoading={isLoading}>
				<StringInput disabled={true} name="shipmentID" />
			</FormItem>

			<FormItem colSpan={defaultColSpan} label="priceToBeCharged" isLoading={isLoading}>
				<CurrencyInput
					name="priceToBeCharged"
					disabled={true}
					value={watch('currency')}
					setValue={(value) => {
						setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
					}}
					currencyErrorMessage={errors?.currency?.message}
				/>
			</FormItem>

			{isExtraCharge && (
				<FormItem colSpan={defaultColSpan} label="cutbackAmount" isLoading={isLoading} required>
					<CurrencyInput
						name="cutbackAmount"
						value={watch('currency')}
						setValue={(value) => {
							setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
						}}
						currencyErrorMessage={errors?.currency?.message}
					/>
				</FormItem>
			)}

			{isExtraCharge === false && (
				<FormItem colSpan={defaultColSpan} label="extraCharge" isLoading={isLoading} required>
					<CurrencyInput
						name="extraCharge"
						value={watch('currency')}
						setValue={(value) => {
							setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
						}}
						currencyErrorMessage={errors?.currency?.message}
					/>
				</FormItem>
			)}

			<FormItem colSpan={defaultColSpan} label="newTotal" isLoading={isLoading}>
				<StringInput
					disabled={true}
					setValue={(value) => {
						setValue(`newTotal`, value, { shouldValidate: true, shouldDirty: true });
					}}
					value={
						watch('recordType') === 'posChargeCorrection' || watch('recordType') === 'underChargeCorrection'
							? Math.max(watch('priceToBeCharged') + watch('extraCharge'), 0)
							: Math.max(watch('priceToBeCharged') - watch('cutbackAmount'), 0)
					}
				/>
			</FormItem>
		</>
	);
};

const ProgressWizardTabs = ({ tab, isLoading, setIsLoading }) => {
	const progressWizardTabs = (tab) => {
		switch (tab) {
			case 0:
				return <ShipmentDetails isLoading={isLoading} setIsLoading={setIsLoading} />;
			case 1:
				return <RecordCalculation isLoading={isLoading} />;
			default:
				return null;
		}
	};

	return progressWizardTabs(tab);
};

const NewRecordForm = NiceModal.create(() => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const [isLoading, setIsLoading] = useState(false);

	const [tab, setTab] = useState(0);

	const schema = useMemo(
		() => [
			yup.object().shape({
				shipmentNumber: yup.string().nullable().required()
			}),

			yup.object().shape({
				recordType: yup.string().nullable().required(),
				recordDescription: yup.string().nullable().required(),
				extraCharge: yup
					.number()
					.nullable()
					.when('recordType', {
						is: (val) => val === 'posChargeCorrection' || val === 'underChargeCorrection',
						then: (schema) => schema.positive().required()
					}),
				cutbackAmount: yup
					.number()
					.nullable()
					.when('recordType', {
						is: (val) =>
							val === 'insuranceChargeBack' || val === 'slaCorrection' || val === 'overChargeCorrection',
						then: (schema) =>
							schema
								.required()
								.test(
									'is-less-than-priceToBeCharged',
									'Cutback amount must be less than priceToBeCharged',
									function (value) {
										const priceToBeCharged = this.resolve(yup.ref('priceToBeCharged'));
										return value === null || value <= priceToBeCharged;
									}
								)
					})
			})
		],
		[]
	);

	const stages = useMemo(
		() => [
			{ name: 'shipmentDetails', icon: faBookOpen },
			{ name: 'recordCalculation', icon: faKey }
		],
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema[tab]),
		defaultValues: {
			shipmentNumber: null,
			recordType: null,
			recordDescription: null,
			cutbackAmount: null,
			extraCharge: null
		}
	});

	const {
		handleSubmit,
		formState: { isDirty }
	} = formMethods;

	const handleCloseModal = () => {
		modal.remove();
	};

	const onSubmit = async (data) => {
		setIsLoading(true);
		axiosPrivate
			.post(SHIPPING_PRICES.ADD_INVOICE_TRANSACTION_RECORD, {
				recordType: data.recordType,
				recordDescription: data.recordDescription,
				cutbackAmount: data.cutbackAmount ?? undefined,
				extraCharge: data.extraCharge ?? undefined,
				shipmentID: data.shipmentID
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.NEW_RECORDS));
					handleCloseModal();
				} else {
					showError();
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const nextStep = () => {
		if (tab < 3 - 1) {
			setTab(tab + 1);
		}
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	return (
		<Modal fullwidth maxWidth="medium" className="modal-newRecordForm">
			<ModalTitle>
				<Typography variant="p1">newRecord</Typography>
			</ModalTitle>

			<ModalContent>
				<ProgressWizard clickable={false} stages={stages} setIndex={setTab} index={tab} gutterBottom />

				<Form formMethods={formMethods} borderless>
					<ProgressWizardTabs tab={tab} isLoading={isLoading} setIsLoading={setIsLoading} />
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{tab !== 0 && (
					<Button
						label="previous"
						variant="outline"
						icon={faAngleLeft}
						onClick={() => prevStep()}
						loading={isLoading}
					/>
				)}
				{tab !== 1 && (
					<Button
						icon={tab !== 2 && faAngleRight}
						label="continue"
						iconPos="right"
						onClick={handleSubmit(nextStep)}
						variant="blue"
						loading={isLoading}
					/>
				)}
				{tab === 1 && (
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						className="saveButton"
						label="save"
						iconPos="right"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						variant="blue"
						loading={isLoading}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default NewRecordForm;
